import React from 'react';
import { Container, Button } from 'react-bootstrap';
import { Home, RefreshCw, Mail } from 'lucide-react';

/**
 * Error Boundary Component
 * Catches React errors in the component tree and displays a fallback UI
 * Prevents white screen of death when errors occur
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // You can also log the error to an error reporting service
    // Example: logErrorToService(error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleRefresh = () => {
    // Clear error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and navigate home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px'
        }}>
          <Container>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '60px 40px',
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Error Icon */}
              <div style={{
                width: '100px',
                height: '100px',
                margin: '0 auto 30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
                color: 'white',
                fontWeight: 'bold'
              }}>
                !
              </div>

              {/* Error Message */}
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#1a202c',
                marginBottom: '20px'
              }}>
                Oops! Something Went Wrong
              </h1>

              <p style={{
                fontSize: '18px',
                color: '#4a5568',
                marginBottom: '30px',
                lineHeight: '1.6'
              }}>
                We're sorry for the inconvenience. An unexpected error has occurred. 
                Our team has been notified and we're working to fix it.
              </p>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '30px'
              }}>
                <Button
                  onClick={this.handleRefresh}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <RefreshCw size={20} />
                  Refresh Page
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline-primary"
                  style={{
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    borderWidth: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <Home size={20} />
                  Go to Home
                </Button>
              </div>

              {/* Contact Support */}
              <div style={{
                borderTop: '1px solid #e2e8f0',
                paddingTop: '20px',
                marginTop: '20px'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: '#718096',
                  marginBottom: '10px'
                }}>
                  Still having problems?
                </p>
                <a
                  href="/contact"
                  style={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                  onMouseLeave={(e) => e.target.style.color = '#667eea'}
                >
                  <Mail size={18} />
                  Contact Support
                </a>
              </div>

              {/* Development Error Details (only show in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details style={{
                  marginTop: '30px',
                  textAlign: 'left',
                  background: '#f7fafc',
                  padding: '20px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}>
                  <summary style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    color: '#2d3748',
                    marginBottom: '10px'
                  }}>
                    Error Details (Development Only)
                  </summary>
                  <div style={{
                    color: '#e53e3e',
                    marginBottom: '10px',
                    fontFamily: 'monospace'
                  }}>
                    {this.state.error.toString()}
                  </div>
                  <pre style={{
                    overflow: 'auto',
                    background: '#2d3748',
                    color: '#e2e8f0',
                    padding: '15px',
                    borderRadius: '5px',
                    fontSize: '11px',
                    lineHeight: '1.5'
                  }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </Container>
        </div>
      );
    }

    // When there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
